import React, { useState, useMemo } from "react";
import { useAuth } from "@/contexts/auth/useAuth";
import { useDailyOvertimeRecords } from "@/hooks/useQueryHooks";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw, Calendar, Building2, Clock, UserCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { exportSecureCSV, type CsvExportConfig } from "@/utils/csvSecurity";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, DataTableColumn } from "@/components/ui/DataTable";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserRole, TimeRange, OvertimeStatus } from "@/types/enums";
import { getStatusColor, StatusBadge } from "@/utils/statusUtils";
import { MONTH_OPTIONS } from "@/constants/months";
import { generateYearOptions, matchesYearFilter } from "@/constants/years";

// Types for overtime data
type DayWiseData = {
  date: string;
  employeeId: string;
  employeeDisplayId: string; // Human-readable employee ID from employees table
  employeeName: string;
  department: string;
  hours: number;
  status: string;
  createdByName?: string | null;
  approverName?: string | null;
  dateCreated?: string | null;
  justification?: string | null;
  reasonCategory?: string | null;
  reason?: string | null;
};

// New type for flattened monthly rows
// Each row is one employee for one month
export type EmployeeMonthRow = {
  employeeId: string;
  employeeDisplayId: string; // Human-readable employee ID from employees table
  employeeName: string;
  department: string;
  month: string; // e.g., "Jan 2024"
  hours: number;
};

const Audit = () => {
  const { user } = useAuth();
  
  // Fetch daily overtime records for the user's company (and department if department_head)
  const companyId = user?.company_id;
  const userRole = user?.role;
  
  // Normalize user.department to always be string[]
  const userAuthDept = user?.department;
  const userDept = useMemo(() => {
    if (typeof userAuthDept === 'string') {
      return userAuthDept.split(',').map(d => d.trim()).filter(d => d);
    }
    return Array.isArray(userAuthDept) ? userAuthDept : [];
  }, [userAuthDept]);
  
  // Only pass department if department_head, superintendent, or plant_manager and userDept has entries
  const { data: overtimeRecordsResponse, isLoading, error, refetch } = useDailyOvertimeRecords({ 
    companyId: companyId || '', // Empty string if undefined
    department: (userRole === UserRole.DEPARTMENT_HEAD || userRole === UserRole.SUPERINTENDENT || userRole === UserRole.PLANT_MANAGER) && userDept.length > 0 ? userDept : undefined,
    enabled: !!companyId // Only run query if companyId exists
  });
  
  // Function to handle data refresh
  const handleRefresh = async () => {
    await refetch();
  };

  // Filter state
  const [selectedMonth, setSelectedMonth] = useState<string>(TimeRange.ALL);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('current');

  // Use shared month options constant
  const monthOptions = MONTH_OPTIONS;

  // Process the data from the response
  const overtimeRecords = useMemo(() => {
    if (!overtimeRecordsResponse) {
      return [];
    }
    
    // The edge function returns data in a nested structure
    const dailyRecords = overtimeRecordsResponse.data || overtimeRecordsResponse;
    
    if (!dailyRecords) {
      return [];
    }
    
    const records = [];
    
    // Handle the grouped records by date
    if (typeof dailyRecords === 'object' && !Array.isArray(dailyRecords)) {
      // Flatten the grouped records
      for (const date in dailyRecords) {
        if (Array.isArray(dailyRecords[date])) {
          dailyRecords[date].forEach(record => {
            // Add the date to each record
            records.push({
              ...record,
              date
            });
          });
        }
      }
    } else if (Array.isArray(dailyRecords)) {
      // If it's already an array, just use it
      return dailyRecords;
    }
    
    return records;
  }, [overtimeRecordsResponse]);
  
  // Generate available years from data
  const availableYears = useMemo(() => {
    const dates = overtimeRecords.map((rec: { date?: string }) => rec.date).filter(Boolean) as string[];
    return generateYearOptions(dates);
  }, [overtimeRecords]);

  // Map backend records to day-wise data rows
  const dayWiseData: DayWiseData[] = useMemo(() => {
    if (!overtimeRecords || overtimeRecords.length === 0) {
      return [];
    }
    
    return overtimeRecords
      .filter((rec: unknown): rec is { date: string; status?: string; department?: string; employee_id?: string; employee_name?: string; employees?: unknown; hours?: number; employee_display_id?: string } => {
        if (typeof rec !== 'object' || rec === null) return false;
        return 'status' in rec && typeof (rec as { status?: string }).status === 'string';
      })
      .filter((rec) => {
        // Month filtering
        if (selectedMonth !== TimeRange.ALL) {
          try {
            const month = new Date(rec.date).getMonth();
            if (month !== parseInt(selectedMonth)) return false;
          } catch (e) {
            console.error('Error parsing date:', rec.date, e);
            return false;
          }
        }
        
        // Year filtering
        if (!matchesYearFilter(rec.date, selectedYear)) {
          return false;
        }
        
        return true;
      })
      // Add status filtering
      .filter((rec) => {
        if (selectedStatus === 'all') return true;
        return rec.status === selectedStatus;
      })
      // Add department filtering for department_head and superintendent
      .filter((rec) => {
        if ((userRole === UserRole.DEPARTMENT_HEAD || userRole === UserRole.SUPERINTENDENT) && userDept && userDept.length > 0) {
          return userDept.includes(rec.department);
        }
        return true;
      })
      .map((rec) => {
        // Handle nested employee data structure
        let employeeName = rec.employee_name || 'Unknown';
        let department = rec.department || 'Unknown';
        
        // If employees data is available in the nested structure
        if (rec.employees) {
          const employeeData = rec.employees;
          if (Array.isArray(employeeData) && employeeData.length > 0) {
            const emp = employeeData[0];
            if (typeof emp === 'object' && emp !== null) {
              const empObj = emp as { name?: string; department?: string };
              employeeName = empObj.name || employeeName;
              department = empObj.department || department;
            }
          }
        }

        type RecWithOptionalFields = {
          created_by_name?: string | null;
          approver_name?: string | null;
          submitted_at?: string | null;
          justification?: string | null;
          reason_category?: string | null;
          reason?: string | null;
          [key: string]: unknown;
        };

        const recTyped = rec as RecWithOptionalFields;
        return {
          date: rec.date,
          employeeId: rec.employee_id,
          employeeDisplayId: rec.employee_display_id || 'Unknown',
          employeeName: employeeName,
          department: department,
          hours: rec.hours || 0,
          status: rec.status || OvertimeStatus.PENDING,
          createdByName: recTyped.created_by_name ?? null,
          approverName: recTyped.approver_name ?? null,
          dateCreated: recTyped.submitted_at ?? null,
          justification: recTyped.justification ?? null,
          reasonCategory: recTyped.reason_category ?? null,
          reason: recTyped.reason ?? null,
        };
      });
  }, [overtimeRecords, selectedMonth, selectedYear, selectedStatus, userRole, userDept]);

  // Function to handle secure CSV download
  const handleDownload = async () => {
    try {
      const headers = ['Status', 'Overtime date', 'Date created', 'Employee', 'Employee ID', 'Department', 'Hours', 'Created By', 'Approver', 'Reason Category', 'Reason'];
      const csvData = dayWiseData.map(item => [
        item.status,
        item.date,
        item.dateCreated ? new Date(item.dateCreated).toLocaleString() : '-',
        item.employeeName,
        item.employeeDisplayId,
        item.department,
        item.hours,
        item.createdByName || '-',
        item.approverName || '-',
        item.reasonCategory || '-',
        item.reason || '-'
      ]);

      const config: CsvExportConfig = {
        filename: 'overtime-audit',
        headers,
        preventInjection: true
      };

      await exportSecureCSV(csvData, config);
      toast.success('Downloaded overtime-audit.csv successfully');
    } catch (error) {
      toast.error('Failed to download data');
      console.error('Download error:', error);
    }
  };

  // Define columns for the data table
  const columns: DataTableColumn<DayWiseData>[] = [
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      searchable: true,
      className: 'min-w-[120px] max-w-[140px]',
      render: (value) => (
        <div className="flex justify-center">
          <StatusBadge status={String(value)} showIcon={true} />
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'date',
      header: 'Overtime Date',
      sortable: true,
      searchable: true,
      className: 'min-w-[100px] max-w-[120px]',
      render: (value) => (
        <div className="text-center font-medium">
          <div className="flex items-center justify-center gap-1 whitespace-normal break-words">
            <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-xs">{String(value)}</span>
          </div>
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'dateCreated',
      header: 'Date Created',
      sortable: true,
      searchable: false,
      className: 'min-w-[100px] max-w-[120px]',
      render: (value) => (
        <div className="text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-1 whitespace-normal break-words">
            <Clock className="h-3 w-3 flex-shrink-0" />
            <span className="text-xs">{value ? new Date(String(value)).toLocaleDateString() : '-'}</span>
          </div>
        </div>
      ),
      exportRender: (value) => value ? new Date(String(value)).toLocaleString() : '-'
    },
    {
      key: 'employeeName',
      header: 'Employee',
      sortable: true,
      searchable: true,
      className: 'min-w-[150px] max-w-[200px] text-left',
      render: (value, row) => {
        const getInitials = (name: string): string => {
          if (!name) return '?';
          const names = name.trim().split(' ');
          if (names.length === 1) return names[0].charAt(0).toUpperCase();
          return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
        };

        return (
          <div className="flex items-center space-x-2">
            <Avatar className="h-8 w-8 flex-shrink-0 ring-2 ring-background border-2 border-border">
              <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 font-semibold text-xs">
                {getInitials(String(value))}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-foreground text-xs whitespace-normal break-words">{String(value)}</span>
              <span className="text-xs text-muted-foreground whitespace-normal break-words">
                ID: {row.employeeDisplayId || 'Unknown'}
              </span>
            </div>
          </div>
        );
      },
      exportRender: (value) => String(value)
    },
    {
      key: 'employeeDisplayId',
      header: 'Employee ID',
      sortable: true,
      searchable: true,
      hidden: true,
      hideFromDropdown: true,
      className: 'min-w-[100px] max-w-[120px]',
      render: (value) => (
        <div className="text-center font-mono text-xs bg-muted/50 px-2 py-1 rounded border whitespace-normal break-words">
          {String(value || 'Unknown')}
        </div>
      ),
      exportRender: (value) => String(value || 'Unknown')
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      searchable: true,
      className: 'min-w-[100px] max-w-[150px]',
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 whitespace-normal break-words text-xs">
            <Building2 className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="whitespace-normal break-words">{String(value)}</span>
          </Badge>
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'hours',
      header: 'Hours',
      sortable: true,
      searchable: false,
      className: 'min-w-[80px] max-w-[100px]',
      render: (value) => (
        <div className="text-center">
          <Badge variant="secondary" className={`px-2 py-1 font-semibold text-xs whitespace-normal break-words ${getStatusColor('info', 'light')}`}>
            <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="whitespace-normal break-words">{Number(value)}h</span>
          </Badge>
        </div>
      ),
      exportRender: (value) => Number(value)
    },
    {
      key: 'createdByName',
      header: 'Created By',
      sortable: true,
      searchable: true,
      className: 'min-w-[100px] max-w-[150px]',
      render: (value) => (
        <div className="text-center font-medium">
          <div className="flex items-center justify-center gap-1 whitespace-normal break-words">
            <UserCheck className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-xs whitespace-normal break-words">{String(value || '-')}</span>
          </div>
        </div>
      ),
      exportRender: (value) => String(value || '-')
    },
    {
      key: 'approverName',
      header: 'Approver',
      sortable: true,
      searchable: true,
      className: 'min-w-[100px] max-w-[150px]',
      render: (value) => (
        <div className="text-center font-medium">
          <div className="flex items-center justify-center gap-1 whitespace-normal break-words">
            <ShieldCheck className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-xs whitespace-normal break-words">{String(value || '-')}</span>
          </div>
        </div>
      ),
      exportRender: (value) => String(value || '-')
    },
    {
      key: 'reasonCategory',
      header: 'Reason Category',
      sortable: true,
      searchable: true,
      className: 'min-w-[120px] max-w-[150px]',
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className={`whitespace-normal break-words text-xs ${getStatusColor('info', 'light')}`}>
            {String(value || '-')}
          </Badge>
        </div>
      ),
      exportRender: (value) => String(value || '-')
    },
    {
      key: 'reason',
      header: 'Reason',
      sortable: true,
      searchable: true,
      className: 'min-w-[150px] max-w-[200px]',
      render: (value) => (
        <div className="text-center">
          <div className="text-sm text-muted-foreground whitespace-normal break-words px-2">
            {String(value || '-')}
          </div>
        </div>
      ),
      exportRender: (value) => String(value || '-')
    }
  ];

  // Show error if there is one
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error Loading Overtime Data</h3>
          <p className="text-red-600 mt-2">{error.toString()}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-48" />
            <div className="flex gap-2">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-32" />
            </div>
          </div>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="space-y-6">
        {/* Filter Controls */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Month:</span>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Year:</span>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Status:</span>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value={OvertimeStatus.APPROVED}>Approved</SelectItem>
                  <SelectItem value={OvertimeStatus.PENDING}>Pending</SelectItem>
                  <SelectItem value={OvertimeStatus.REJECTED}>Rejected</SelectItem>
                  <SelectItem value={OvertimeStatus.PENDING_VP_APPROVAL}>Pending VP</SelectItem>
                  <SelectItem value={OvertimeStatus.PENDING_DH_APPROVAL}>Pending DH</SelectItem>
                  <SelectItem value={OvertimeStatus.PENDING_PM_APPROVAL}>Pending PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleDownload} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </Button>
          </div>
        </div>

        {/* Data Table */}
        <div className="w-full overflow-hidden">
          <div className="overflow-x-auto">
            <DataTable
              data={dayWiseData}
              columns={columns}
              title="System Audit"
              isLoading={isLoading}
              config={{
                searchPlaceholder: "Search by employee, ID, department, or creator...",
                defaultSortField: 'date',
                defaultSortDirection: 'desc',
                rowsPerPage: 10,
                showSearch: true,
                showColumnVisibility: true,
                showExport: false, // We have custom export
                showRefresh: false, // We have custom refresh
                emptyStateMessage: "No overtime records found",
                noSearchResultsMessage: "No records match your search criteria"
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Audit;
